const STORAGE_KEY = "relook_device_credentials";

export const platformAuthenticatorAvailable = async () => {
	if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false;
	try {
		return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
	} catch (error) {
		return false;
	}
};

const readStore = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
		return parsed && typeof parsed === "object" ? parsed : {};
	} catch (error) {
		return {};
	}
};

export const localDeviceIds = (email) => {
	const ids = readStore()[String(email || "").toLowerCase()];
	return Array.isArray(ids) ? ids.filter(Boolean) : [];
};

export const rememberDeviceId = (email, credentialId) => {
	const key = String(email || "").toLowerCase();
	if (!key || !credentialId) return;
	const store = readStore();
	const ids = new Set(localDeviceIds(key));
	ids.add(credentialId);
	store[key] = [...ids];
	localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
};

export const forgetDeviceId = (email, credentialId) => {
	const key = String(email || "").toLowerCase();
	const store = readStore();
	store[key] = localDeviceIds(key).filter((id) => id !== credentialId);
	localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
};

const base64urlToBuffer = (value) => {
	const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`.replace(/-/g, "+").replace(/_/g, "/");
	const binary = atob(padded);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
	return bytes.buffer;
};

const bufferToBase64url = (value) => {
	const bytes = new Uint8Array(value);
	let binary = "";
	bytes.forEach((byte) => {
		binary += String.fromCharCode(byte);
	});
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const credentialDescriptor = (item) => ({
	type: "public-key",
	id: base64urlToBuffer(item.id),
	transports: ["internal"],
});

const platformSelection = {
	authenticatorAttachment: "platform",
	residentKey: "discouraged",
	requireResidentKey: false,
	userVerification: "required",
};

export const createDeviceCredential = async (options) => {
	const publicKey = {
		challenge: base64urlToBuffer(options.challenge),
		rp: options.rp,
		user: {
			id: base64urlToBuffer(options.user.id),
			name: options.user.name,
			displayName: options.user.displayName,
		},
		pubKeyCredParams: options.pubKeyCredParams,
		timeout: options.timeout,
		attestation: "none",
		authenticatorSelection: platformSelection,
		excludeCredentials: (options.excludeCredentials || []).map(credentialDescriptor),
		hints: ["client-device"],
	};
	const credential = await navigator.credentials.create({ publicKey });
	if (credential?.authenticatorAttachment === "cross-platform") {
		throw new Error("Use this laptop's fingerprint, PIN, or password. Security keys are not accepted.");
	}
	return credential;
};

export const getDeviceCredential = async (options) => {
	const publicKey = {
		challenge: base64urlToBuffer(options.challenge),
		rpId: options.rpId,
		timeout: options.timeout || 60000,
		userVerification: "required",
		allowCredentials: (options.allowCredentials || []).map(credentialDescriptor),
		hints: ["client-device"],
	};
	const credential = await navigator.credentials.get({ publicKey });
	if (credential?.authenticatorAttachment === "cross-platform") {
		throw new Error("Use this laptop's fingerprint, PIN, or password. Security keys are not accepted.");
	}
	return credential;
};

export const serializeRegistration = (credential) => ({
	id: credential.id,
	rawId: bufferToBase64url(credential.rawId),
	type: credential.type,
	authenticatorAttachment: credential.authenticatorAttachment || "",
	response: {
		clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
		attestationObject: bufferToBase64url(credential.response.attestationObject),
	},
});

export const serializeAssertion = (credential) => ({
	id: credential.id,
	rawId: bufferToBase64url(credential.rawId),
	type: credential.type,
	authenticatorAttachment: credential.authenticatorAttachment || "",
	response: {
		clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
		authenticatorData: bufferToBase64url(credential.response.authenticatorData),
		signature: bufferToBase64url(credential.response.signature),
	},
});
